import { typeValidator } from '@chilipiper/utils'
import React, { RefObject, ReactNode, useEffect, useRef } from 'react'
import { useLocale, useNumberField, mergeProps } from 'react-aria'
import { useNumberFieldState } from 'react-stately'
import styled, { css } from 'styled-components'
import { layout } from 'styled-system'
import invariant from 'tiny-invariant'
import { IconError } from '@chilipiper/icons/figma-generated/src/icon'
import { Icon } from '../../../../old/icon'
import { Box, Text } from '../../../core-components'
import { getTextStyleCss } from '../../../core-components/text/helpers'
import { validatePositioningProps } from '../../../helpers/validatePositioningProps'
import { Events } from '../../../types'
import { FocusableHandlerProps, PositioningProps } from '../../types'
import { SettingLabel, SettingLabelContainer } from '../../wayfinding/setting-label/SettingLabel'
import { InputNumberArrows } from './InputNumberArrows'

type Variant = 'regular' | 'nested'

type Props = {
  'data-id'?: string
  'data-test-id'?: string
  errorMessage?: string
  eventsRef?: RefObject<Events<'focus'> | null | undefined>
  isDisabled?: boolean
  isRequired?: boolean
  label: ReactNode
  maxValue?: number
  minValue?: number
  name?: string
  onChange: (value?: number) => void
  placeholder: string
  step?: number
  value?: number
  variant?: Variant
  autoComplete?: 'on' | 'off'
  size?: 'regular' | 'large'
} & PositioningProps &
  FocusableHandlerProps

export const InputNumber = ({
  value,
  onChange,
  onBlur,
  onFocus,
  step,
  minValue,
  maxValue,
  name,
  label,
  placeholder,
  errorMessage,
  isDisabled,
  isRequired,
  'data-id': dataId,
  'data-test-id': testId,
  eventsRef,
  variant = 'regular',
  autoComplete,
  size = 'regular',
  ...positioningProps
}: Props) => {
  validatePositioningProps(positioningProps, 'InputNumber')

  const { text: labelText, description } = parseLabel(label)
  const { locale } = useLocale()
  const state = useNumberFieldState({
    isDisabled,
    locale,
    minValue,
    maxValue,
    step,
    value: value ?? Number.NaN,
    onChange: inputValue => onChange(Number.isNaN(inputValue) ? undefined : inputValue),
  })

  const inputRef = useRef<HTMLInputElement>(null)
  const {
    labelProps,
    groupProps,
    inputProps,
    incrementButtonProps,
    decrementButtonProps,
    errorMessageProps,
    descriptionProps,
  } = useNumberField(
    {
      isDisabled,
      validationState: errorMessage ? 'invalid' : 'valid',
      isRequired,
      placeholder,
      label: labelText,
      description: description,
      errorMessage,
      onBlur,
      onFocus,
    },
    state,
    inputRef
  )

  useEffect(() => {
    if (eventsRef) {
      eventsRef.current = {
        focus: () => {
          setTimeout(() => inputRef.current?.focus(), 0)
        },
      }
      return () => (eventsRef.current = undefined)
    }
  }, [eventsRef])

  return (
    <SettingLabelContainer
      label={label}
      labelElement='label'
      isRequired={isRequired}
      labelProps={labelProps}
      descriptionProps={descriptionProps}
      {...positioningProps}
    >
      <Box width='100%'>
        <InputGroup
          {...groupProps}
          variant={variant}
          hasError={!!errorMessage}
          data-test-id={testId && `${testId}-group`}
        >
          <StyledInput
            variant={variant}
            hasError={!!errorMessage}
            $size={size}
            name={name}
            {...mergeProps(inputProps, {
              // We want to commit as the user leaves the input as they might want to click somewhere
              // It's possible that a last minute change before they click would result in a layout shift or a button getting disabled
              onMouseLeave: () => {
                state.commit()
              },
            })}
            ref={inputRef}
            data-id={dataId}
            autoComplete={autoComplete}
            data-test-id={testId}
          />
          {!!errorMessage && (
            <Icon
              icon={IconError}
              color='fg/caution'
              position='absolute'
              w={4}
              h={4}
              top={variant === 'regular' ? 3 : 4}
              mr={1}
              right={8}
            />
          )}
          <Container variant={variant}>
            <InputNumberArrows
              variant={variant}
              data-test-id={testId}
              onIncrement={incrementButtonProps}
              onDecrement={decrementButtonProps}
            />
          </Container>
        </InputGroup>
        {!!errorMessage && (
          <Text
            {...errorMessageProps}
            display='block'
            textStyle='body-multiline-tight'
            data-test-id='input-error-message'
            color='fg/caution'
            mt={2}
          >
            {errorMessage}
          </Text>
        )}
      </Box>
    </SettingLabelContainer>
  )
}

const parseLabel = (label: React.ReactNode) => {
  invariant(typeValidator(InputLabel)(label), `Input: label must be an InputNumber.Label component`)
  return {
    text: label.props.children,
    description: label.props.description,
  }
}
const InputLabel = (props: React.ComponentProps<typeof SettingLabel>) => <SettingLabel {...props} />
InputNumber.Label = InputLabel

const Container = styled(Box)<{ variant: Variant }>`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: ${({ theme }) => theme.sizes[7]};
  height: 100%;
  border-radius: ${({ theme, variant }) =>
    variant === 'regular' ? `0 ${theme.radii[10]} ${theme.radii[10]} 0` : undefined};
  box-shadow: ${({ theme, variant }) =>
    variant === 'regular'
      ? `${theme.shadows['border/cell']}, ${theme.shadows['border/row-top']}, ${theme.shadows['border/row']}`
      : undefined};
`

const InputGroup = styled.div<{ hasError: boolean; variant: Variant }>`
  position: relative;
  width: 100%;
  height: ${({ theme, variant }) => theme.sizes[variant === 'regular' ? 8 : 9]};

  input {
    background: transparent;
    box-shadow: ${({ theme, variant }) =>
      variant === 'regular'
        ? `${theme.shadows['border/cell-left']}, ${theme.shadows['border/row-top']}, ${theme.shadows['border/row']}`
        : undefined};
  }

  :hover {
    input {
      background: ${({ theme }) => theme.colors['bg/form-item-hovered']};
      box-shadow: none;
    }
  }

  input:disabled {
    background: ${({ theme }) => theme.colors['bg/disabled']};
    box-shadow: none;
  }

  :hover input:focus,
  input:focus {
    background: ${({ theme }) => theme.colors['bg/form-item-focused']};
    box-shadow: ${({ theme, variant }) =>
      theme.shadows[
        variant === 'regular' ? 'border/form-item-focused' : 'border/form-item-nested-focused'
      ]};

    ${Container} {
      > div {
        box-shadow: none;
      }
    }
  }

  ${({ hasError }) =>
    hasError &&
    css`
      input {
        background: ${({ theme }) => theme.colors['bg/form-item-error']};
        box-shadow: none;
      }
    `}
`

const StyledInput = styled.input<{
  hasError: boolean
  variant: Variant
  $size: 'regular' | 'large'
}>`
  ${layout}
  border: none;
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  display: block;
  width: ${({ theme }) => `calc(100% - ${theme.space[7]})`};
  height: ${({ theme, variant }) => theme.sizes[variant === 'regular' ? 8 : 9]};

  right: ${({ theme }) => theme.space[6]};
  border-radius: ${({ theme, variant }) =>
    variant === 'regular' ? `${theme.radii[10]} 0 0 ${theme.radii[10]}` : undefined};
  padding: ${({ theme, hasError }) => `0 ${theme.space[hasError ? 9 : 4]} 0 ${theme.space[5]}`};

  transition:
    box-shadow 0.2s ease-in-out,
    background 0.2s ease-in-out;

  ${({ $size }) => getTextStyleCss($size === 'large' ? 'form-value-l' : 'form-value')}
  color: ${({ theme }) => theme.colors['fg/major']};
  text-overflow: ellipsis;

  ::placeholder {
    ${({ $size }) => getTextStyleCss($size === 'large' ? 'form-value-l' : 'form-value')}
    color: ${({ theme }) => theme.colors['fg/minor']};
  }

  :focus {
    outline: none;
  }
  :disabled {
    color: ${({ theme }) => theme.colors['fg/disabled']};

    ::placeholder {
      color: ${({ theme }) => theme.colors['fg/disabled']};
    }
  }
`
